// Make sure this file is treated as a module
export {};

// Define the available languages
export const AVAILABLE_LANGUAGES = {
  en: 'English',
  pl: 'Polski',
  fr: 'Français',
  de: 'Deutsch',
  es: 'Español',
  no: 'Norsk'
};

// Define a custom event for language changes
export const LANGUAGE_CHANGE_EVENT = 'app-language-changed';
export const TRANSLATIONS_LOADED_EVENT = 'app-translations-loaded';

// Type definition for translations
export type TranslationSet = Record<string, string>;

// Cache for translations to avoid repeated fetching
const translationsCache: Record<string, TranslationSet> = {};

/**
 * Helper function to detect the browser language
 * Returns the language code if supported in our available languages, otherwise 'en'
 */
function detectBrowserLanguage(): string {
  // Get browser language
  const browserLang = navigator.language.split('-')[0].toLowerCase();
  
  // Check if this language is supported in our application
  if (Object.keys(AVAILABLE_LANGUAGES).includes(browserLang)) {
    return browserLang;
  }
  
  // Default to English if browser language not supported
  return 'en';
}

class TranslationService {
  private translations: TranslationSet = {};
  private currentLanguage: string = 'en';
  private initialized: boolean = false;
  private translationLoadPromise: Promise<void> | null = null;

  constructor() {
    // Initialize the service
    this.init().catch(error => {
      console.error('Failed to initialize TranslationService:', error);
    });
  }

  private async init(): Promise<void> {
    try {
      // Get the stored language or detect browser language
      const storedLanguage = localStorage.getItem('language');
      
      // Ensure the stored language is valid, or use browser language
      if (storedLanguage && Object.keys(AVAILABLE_LANGUAGES).includes(storedLanguage)) {
        this.currentLanguage = storedLanguage;
      } else {
        // If invalid or not set, detect browser language or default to English
        this.currentLanguage = detectBrowserLanguage();
        localStorage.setItem('language', this.currentLanguage);
      }

      // Set the Accept-Language header for API requests
      document.cookie = `Accept-Language=${this.currentLanguage}; path=/; max-age=31536000`;

      // Fetch the translation file
      await this.loadTranslations(this.currentLanguage);
      this.initialized = true;
      
      // Notify any listeners that translations are now loaded
      window.dispatchEvent(new CustomEvent(TRANSLATIONS_LOADED_EVENT, { 
        detail: { 
          language: this.currentLanguage, 
          translationCount: Object.keys(this.translations).length 
        } 
      }));
    } catch (error) {
      console.error('Failed to initialize translations:', error);
      this.initialized = true; // Still mark as initialized to avoid blocking
      
      // Notify listeners even in case of error
      window.dispatchEvent(new CustomEvent(TRANSLATIONS_LOADED_EVENT, { 
        detail: { 
          language: this.currentLanguage, 
          translationCount: Object.keys(this.translations).length, 
          error: true 
        } 
      }));
    }
  }

  private async loadTranslations(lang: string): Promise<void> {
    // Return cached translations if available
    if (translationsCache[lang]) {
      console.log(`Using cached translations for ${lang}`);
      this.translations = translationsCache[lang];
      return;
    }

    try {
      // Add a timestamp to prevent caching
      const response = await fetch(`/locales/${lang}.json?_t=${Date.now()}`);
      
      if (!response.ok) {
        throw new Error(`Failed to load translations: ${response.status} ${response.statusText}`);
      }
      
      const data = await response.json();
      
      // Cache the translations
      translationsCache[lang] = data;
      this.translations = data;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      console.error(`Could not fetch translations from server: ${errorMessage}`);
      
      // Fall back to empty translations object
      this.translations = {};
    }
  }

  public async setLanguage(lang: string): Promise<void> {
    // If there's already a load in progress, wait for it
    if (this.translationLoadPromise) {
      await this.translationLoadPromise;
    }

    try {
      // Make sure service is initialized
      if (!this.initialized) {
        await this.waitForInitialization();
      }

      // Validate language
      if (!Object.keys(AVAILABLE_LANGUAGES).includes(lang)) {
        console.error(`Language ${lang} is not supported`);
        return;
      }

      // Skip if same language
      if (lang === this.currentLanguage) {
        return;
      }
      
      // Begin loading translations
      const loadPromise = this.loadTranslations(lang);
      this.translationLoadPromise = loadPromise;
      
      // Wait for translations to load
      await loadPromise;
      this.translationLoadPromise = null;
      
      // Update settings after translations are loaded
      localStorage.setItem('language', lang);
      this.currentLanguage = lang;
      
      // Update the Accept-Language header for API requests
      document.cookie = `Accept-Language=${lang}; path=/; max-age=31536000`;
      
      // Dispatch custom event to notify components about language change
      window.dispatchEvent(new CustomEvent(LANGUAGE_CHANGE_EVENT, { 
        detail: { 
          language: lang,
          translationCount: Object.keys(this.translations).length
        } 
      }));
      
      // Don't reload page, let components react to the event
    } catch (error) {
      console.error('Failed to change language:', error);
      this.translationLoadPromise = null;
    }
  }

  // Helper function to wait for initialization
  public waitForInitialization(): Promise<void> {
    if (this.initialized) {
      return Promise.resolve();
    }
    
    return new Promise((resolve) => {
      const checkInterval = setInterval(() => {
        if (this.initialized) {
          clearInterval(checkInterval);
          resolve();
        }
      }, 100);
    });
  }

  public isInitialized(): boolean {
    return this.initialized;
  }

  public hasTranslations(): boolean {
    const hasKeys = Object.keys(this.translations).length > 0;
    console.log(`hasTranslations check: ${hasKeys} (${Object.keys(this.translations).length} keys)`);
    return hasKeys;
  }

  public getCurrentLanguage(): string {
    return this.currentLanguage;
  }

  public getTranslations(): TranslationSet {
    return this.translations;
  }

  public translate(key: string, params?: Record<string, string>): string {
    let translation = this.translations[key] || key;
    
    // Replace any parameters in the translation
    if (params) {
      Object.entries(params).forEach(([paramKey, paramValue]) => {
        translation = translation.replace(`{${paramKey}}`, paramValue);
      });
    }
    
    return translation;
  }
}

// Create and export a singleton instance
const translationService = new TranslationService();
export default translationService; 
